import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

/**
 * Landing page for Easebuzz's backend-configured success/failure redirect
 * (surl/furl). Easebuzz sends the browser here via a full-page navigation
 * (not a popup), so this page has to recover the order ref and originating
 * path from wherever they were left before checkout.
 *
 * The order ref is read with this priority:
 *   1. `easebuzzOrderRef` query param — set by the backend's surl/furl
 *      itself (e.g. `/payment/easebuzz-success?easebuzzOrderRef={txnid}`).
 *      This survives even if sessionStorage gets cleared/partitioned across
 *      the cross-origin hop to Easebuzz's hosted checkout and back.
 *   2. `sessionStorage` — set before navigating away in openEasebuzzCheckout
 *      (buy-sell-metals.tsx), used as a fallback when the backend redirect
 *      doesn't carry the order ref.
 *
 * Both success and failure redirects forward `easebuzzStatus` (and the
 * order ref, when available) so the originating page can show the right
 * toast even when payment failed/was cancelled.
 */
type Props = {
  success: boolean;
};

const EasebuzzCallbackBase = ({ success }: Props) => {
  const history = useHistory();

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const orderRef =
      queryParams.get('easebuzzOrderRef') ||
      sessionStorage.getItem('easebuzzOrderRef');
    const returnPath = sessionStorage.getItem('easebuzzReturnPath') || '/dashboard';
    sessionStorage.removeItem('easebuzzReturnPath');
    sessionStorage.removeItem('easebuzzOrderRef');

    const status = success ? 'success' : 'failure';
    // returnPath can already carry a query string (e.g. SIP's
    // /sip-payment-schedule/:id?data=...), so append with '&' in that case.
    const separator = returnPath.includes('?') ? '&' : '?';
    if (orderRef) {
      history.replace(
        `${returnPath}${separator}easebuzzOrderRef=${encodeURIComponent(orderRef)}&easebuzzStatus=${status}`
      );
    } else {
      history.replace(`${returnPath}${separator}easebuzzStatus=${status}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default EasebuzzCallbackBase;